import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Tag, Modal, message, Typography, Card, Row, Col, Statistic } from 'antd';
import { PlusOutlined, PlayCircleOutlined, PauseCircleOutlined, StopOutlined, QrcodeOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { eventApi } from '../services/eventApi';
import EventForm from '../components/EventForm';
import type { EventSession } from '../types';

const { Title } = Typography;
const { confirm } = Modal;

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<EventSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventSession | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    ended: 0
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventApi.getAllEvents();
      console.log('API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response data type:', typeof response.data);
      
      // Handle different response structures
      let eventList: EventSession[] = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        // API returns { success: true, data: [...] }
        eventList = response.data.data;
      } else if (Array.isArray(response.data)) {
        // API returns [...] directly  
        eventList = response.data;
      } else {
        console.warn('Unexpected API response structure:', response.data);
        eventList = [];
      }
      
      console.log('Processed eventList:', eventList);
      setEvents(eventList);
      
      // Calculate stats
      const statsData = {
        total: eventList.length,
        active: eventList.filter((e: EventSession) => e.status === 'active').length,
        inactive: eventList.filter((e: EventSession) => e.status === 'inactive').length,
        ended: eventList.filter((e: EventSession) => e.status === 'ended').length
      };
      setStats(statsData);
    } catch (error) {
      message.error('Không thể tải danh sách sự kiện');
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setIsModalVisible(true);
  };

  const handleEdit = (event: EventSession) => {
    setEditingEvent(event);
    setIsModalVisible(true);
  };

  const handleDelete = (event: EventSession) => {
    confirm({
      title: 'Xác nhận xóa',
      content: `Bạn có chắc chắn muốn xóa sự kiện "${event.eventName}"?`,
      okText: 'Xóa',
      okType: 'danger',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await eventApi.deleteEvent(event.id);
          message.success('Xóa sự kiện thành công');
          fetchEvents();
        } catch (error) {
          message.error('Không thể xóa sự kiện');
          console.error('Error deleting event:', error);
        }
      }
    });
  };

  const handleStatusChange = async (eventId: string, action: 'activate' | 'deactivate' | 'end') => {
    try {
      let successMessage = '';
      
      switch (action) {
        case 'activate':
          await eventApi.activateEvent(eventId);
          successMessage = 'Kích hoạt sự kiện thành công';
          break;
        case 'deactivate':
          await eventApi.deactivateEvent(eventId);
          successMessage = 'Tạm dừng sự kiện thành công';
          break;
        case 'end':
          await eventApi.endEvent(eventId);
          successMessage = 'Kết thúc sự kiện thành công';
          break;
      }
      
      message.success(successMessage);
      fetchEvents();
    } catch (error) {
      message.error('Không thể thay đổi trạng thái sự kiện');
      console.error('Error changing event status:', error);
    }
  };

  const openQRDisplay = (event: EventSession) => {
    // Navigate to QR display page within admin
    navigate(`/events/${event.id}/qr`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'default';
      case 'ended':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Đang hoạt động';
      case 'inactive':
        return 'Tạm dừng';
      case 'ended':
        return 'Đã kết thúc';
      default:
        return status;
    }
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('vi-VN');
  };

  const isEventActive = (event: EventSession) => {
    const now = new Date();
    const startTime = new Date(event.startTime);
    const endTime = new Date(event.endTime);
    return now >= startTime && now <= endTime && event.status === 'active';
  };

  const isEventEnded = (event: EventSession) => {
    const now = new Date();
    const endTime = new Date(event.endTime);
    return now > endTime || event.status === 'ended';
  };

  const columns: ColumnsType<EventSession> = [
    {
      title: 'Tên sự kiện',
      dataIndex: 'eventName',
      key: 'eventName',
      render: (text: string, record: EventSession) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            📍 {record.location}
          </div>
        </div>
      ),
    },
    {
      title: 'Thời gian',
      key: 'time',
      render: (_, record: EventSession) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            <strong>Bắt đầu:</strong> {formatDateTime(record.startTime)}
          </div>
          <div style={{ fontSize: '12px' }}>
            <strong>Kết thúc:</strong> {formatDateTime(record.endTime)}
          </div>
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: EventSession) => {
        const isLive = isEventActive(record);
        return (
          <div>
            <Tag color={getStatusColor(status)}>
              {getStatusText(status)}
            </Tag>
            {isLive && (
              <Tag color="red" style={{ marginTop: 4 }}>
                🔴 ĐANG DIỄN RA
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'QR Refresh',
      dataIndex: 'qrRefreshInterval',
      key: 'qrRefreshInterval',
      render: (interval: number) => `${interval}s`,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDateTime(date),
    },
    {
      title: 'Thao tác',
      key: 'actions',
      render: (_, record: EventSession) => {
        const ended = isEventEnded(record);
        const active = record.status === 'active';
        
        return (
          <Space size="small" wrap>
            <Button
              type="primary"
              size="small"
              icon={<QrcodeOutlined />}
              onClick={() => openQRDisplay(record)}
              disabled={record.status !== 'active'}
              title="Mở trang hiển thị QR"
            >
              QR
            </Button>
            
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleEdit(record)}
              title="Xem chi tiết"
            >
              Xem
            </Button>
            
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={ended}
              title="Chỉnh sửa"
            />
            
            {!ended && (
              <>
                {!active ? (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    onClick={() => handleStatusChange(record.id, 'activate')}
                    title="Kích hoạt"
                  />
                ) : (
                  <Button
                    size="small"
                    icon={<PauseCircleOutlined />}
                    onClick={() => handleStatusChange(record.id, 'deactivate')}
                    title="Tạm dừng"
                  />
                )}
                
                <Button
                  size="small"
                  danger
                  icon={<StopOutlined />}
                  onClick={() => handleStatusChange(record.id, 'end')}
                  title="Kết thúc"
                />
              </>
            )}
            
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              title="Xóa"
            />
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-header">
        <Title level={2}>Quản lý Sự kiện</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
        >
          Tạo sự kiện mới
        </Button>
      </div>

      {/* Statistics Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tổng số sự kiện"
              value={stats.total}
              prefix={<QrcodeOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Đang hoạt động"
              value={stats.active}
              prefix={<PlayCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tạm dừng"
              value={stats.inactive}
              prefix={<PauseCircleOutlined />}
              valueStyle={{ color: '#d46b08' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Đã kết thúc"
              value={stats.ended}
              prefix={<StopOutlined />}
              valueStyle={{ color: '#666' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Events Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={events}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} của ${total} sự kiện`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingEvent ? 'Chỉnh sửa sự kiện' : 'Tạo sự kiện mới'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
        destroyOnClose
      >
        <EventForm
          event={editingEvent}
          onSuccess={() => {
            setIsModalVisible(false);
            fetchEvents();
          }}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>
    </div>
  );
};

export default EventsPage;
